import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { useFeatureFlag } from './FeaturesContext';
import type {
  AsyncTask,
  AsyncTaskProgressEvent,
  AsyncTaskCompletedEvent,
} from 'lib.store/src/type-partner/async-task/models';

const CLEANUP_DELAY = 10_000;

const makeKey = (taskType: string, subjectType: string, subjectId: number) =>
  `${taskType}:${subjectType}:${subjectId}`;

type CompletionCallback = (event: AsyncTaskCompletedEvent) => void;

interface AsyncTaskContextType {
  tasks: AsyncTask[];
  getTasksByThreadId: (threadId: number) => AsyncTask[];
  getTasksBySubject: (subjectType: string, subjectId: number) => AsyncTask[];
  getTask: (taskType: string, subjectType: string, subjectId: number) => AsyncTask | undefined;
  subscribeToCompletion: (
    taskType: string,
    subjectType: string,
    subjectId: number,
    callback: CompletionCallback,
  ) => () => void;
}

const AsyncTaskContext = createContext<AsyncTaskContextType | undefined>(undefined);

interface AsyncTaskProviderProps {
  children: ReactNode;
}

export const AsyncTaskProvider: React.FC<AsyncTaskProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const organizationId = user?.organization?.id || '';
  const isAiAgentEnabled = useFeatureFlag('ai_agent');

  const tasksRef = useRef<Map<string, AsyncTask>>(new Map());
  const [version, setVersion] = useState(0);
  const cleanupTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const subscribersRef = useRef<Map<string, Set<CompletionCallback>>>(new Map());

  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const scheduleCleanup = useCallback(
    (key: string) => {
      const existing = cleanupTimersRef.current.get(key);
      if (existing) clearTimeout(existing);

      const timer = setTimeout(() => {
        tasksRef.current.delete(key);
        cleanupTimersRef.current.delete(key);
        bump();
      }, CLEANUP_DELAY);

      cleanupTimersRef.current.set(key, timer);
    },
    [bump],
  );

  const notifySubscribers = useCallback((key: string, event: AsyncTaskCompletedEvent) => {
    const subs = subscribersRef.current.get(key);
    if (subs) {
      subs.forEach((cb) => cb(event));
    }
  }, []);

  useEffect(() => {
    if (!organizationId || !window.Echo || !isAiAgentEnabled) return;

    const channel = window.Echo.private(`boost.organization.${organizationId}`);

    channel
      .listen('.async_task.progress', (event: AsyncTaskProgressEvent) => {
        const key = makeKey(event.task_type, event.subject_type, event.subject_id);

        // Cancel cleanup if task restarts
        const existingTimer = cleanupTimersRef.current.get(key);
        if (existingTimer) {
          clearTimeout(existingTimer);
          cleanupTimersRef.current.delete(key);
        }

        const task: AsyncTask = {
          key,
          taskType: event.task_type,
          subjectType: event.subject_type,
          subjectId: event.subject_id,
          status: 'in_progress',
          progress: event.progress,
          stage: event.stage,
          message: event.message,
          data: event.data,
          threadId: event.thread_id,
          updatedAt: Date.now(),
        };

        tasksRef.current.set(key, task);
        bump();
      })
      .listen('.async_task.completed', (event: AsyncTaskCompletedEvent) => {
        const key = makeKey(event.task_type, event.subject_type, event.subject_id);

        const existing = tasksRef.current.get(key);
        const task: AsyncTask = {
          key,
          taskType: event.task_type,
          subjectType: event.subject_type,
          subjectId: event.subject_id,
          status: event.success ? 'completed' : 'failed',
          progress: event.success ? 1 : (existing?.progress ?? 0),
          stage: existing?.stage ?? '',
          message: existing?.message ?? '',
          threadId: event.thread_id ?? existing?.threadId,
          error: event.error,
          result: event.result,
          updatedAt: Date.now(),
        };

        tasksRef.current.set(key, task);
        bump();
        notifySubscribers(key, event);
        scheduleCleanup(key);
      });

    return () => {
      channel.stopListening('.async_task.progress');
      channel.stopListening('.async_task.completed');
    };
  }, [organizationId, isAiAgentEnabled, bump, scheduleCleanup, notifySubscribers]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      cleanupTimersRef.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const tasks = Array.from(tasksRef.current.values());

  const getTasksByThreadId = useCallback(
    (threadId: number): AsyncTask[] => {
      void version;
      return Array.from(tasksRef.current.values()).filter((t) => t.threadId === threadId);
    },
    [version],
  );

  const getTasksBySubject = useCallback(
    (subjectType: string, subjectId: number): AsyncTask[] => {
      void version;
      return Array.from(tasksRef.current.values()).filter(
        (t) => t.subjectType === subjectType && t.subjectId === subjectId,
      );
    },
    [version],
  );

  const getTask = useCallback(
    (taskType: string, subjectType: string, subjectId: number): AsyncTask | undefined => {
      void version;
      return tasksRef.current.get(makeKey(taskType, subjectType, subjectId));
    },
    [version],
  );

  const subscribeToCompletion = useCallback(
    (
      taskType: string,
      subjectType: string,
      subjectId: number,
      callback: CompletionCallback,
    ): (() => void) => {
      const key = makeKey(taskType, subjectType, subjectId);
      if (!subscribersRef.current.has(key)) {
        subscribersRef.current.set(key, new Set());
      }
      subscribersRef.current.get(key)!.add(callback);

      return () => {
        const subs = subscribersRef.current.get(key);
        if (subs) {
          subs.delete(callback);
          if (subs.size === 0) {
            subscribersRef.current.delete(key);
          }
        }
      };
    },
    [],
  );

  return (
    <AsyncTaskContext.Provider
      value={{
        tasks,
        getTasksByThreadId,
        getTasksBySubject,
        getTask,
        subscribeToCompletion,
      }}
    >
      {children}
    </AsyncTaskContext.Provider>
  );
};

export const useAsyncTask = () => {
  const context = useContext(AsyncTaskContext);
  if (!context) {
    throw new Error('useAsyncTask must be used within an AsyncTaskProvider');
  }
  return context;
};
